import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ForgotPasswordForm from '../../organisms/ForgotPasswordForm';
import { useRequestResetPassword } from '@/features/auth/hooks/useRequestResetPassword';
import { useAuthStore } from '@/features/auth/store/auth';
import { getFederatedLoginRequired } from '@/shared/utils/apiError';
import type { FederatedLoginLocationState } from '@/features/auth/types/auth.types';
import i18n from '@/shared/i18n';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState<string>('');
  // CA06 — usuário federado identificado: permanece nesta tela com o alerta
  // "SSO Identificado" (Figma [Erro 02]) e a ação primária "Acessar via SSO".
  const [ssoIdentified, setSsoIdentified] =
    useState<FederatedLoginLocationState | null>(null);
  const { setError: setAuthError } = useAuthStore();

  // Clear any existing auth errors on mount
  useEffect(() => {
    setAuthError(null);
  }, [setAuthError]);

  const { mutate: requestResetMutate, isPending } = useRequestResetPassword({
    onSuccess: () => {
      setSuccess(true);
      setError(null);
      setAuthError(null);
    },
    onError: (err, variables) => {
      // CA06 — usuário federado não recupera senha: nenhuma credencial é
      // criada, alterada ou enviada; o acesso da empresa é feito pelo fluxo
      // corporativo, para onde o usuário é conduzido com o e-mail preservado.
      const federated = getFederatedLoginRequired(err);
      if (federated) {
        setError(null);
        setSuccess(false);
        setSsoIdentified({
          email: variables.email,
          reason: 'password-recovery-blocked',
          registrationId: federated.registrationId,
          authenticationUrl: federated.authenticationUrl,
        });
        return;
      }

      const isEmailNotRegistered =
        axios.isAxiosError(err) && err.response?.status === 401;
      const errorMessage = isEmailNotRegistered
        ? i18n.t('Email not registered on the platform')
        : err.message || i18n.t('Failed to send reset link');
      setError(errorMessage);
      setSuccess(false);
    },
  });

  const handleForgotPassword = async (data: { email: string }) => {
    setEmail(data.email);
    requestResetMutate({ email: data.email });
  };

  // CA06 — conduz ao fluxo corporativo sem redigitar o e-mail.
  const handleAccessSso = (typedEmail: string) => {
    const state: FederatedLoginLocationState = {
      ...(ssoIdentified ?? { reason: 'password-recovery-blocked' }),
      email: typedEmail || ssoIdentified?.email,
    };
    navigate('/sso', { state });
  };

  return (
    <ForgotPasswordForm
      onSubmit={handleForgotPassword}
      loading={isPending}
      error={error}
      success={success}
      email={email}
      ssoIdentified={ssoIdentified !== null}
      onAccessSso={handleAccessSso}
    />
  );
};

export default ForgotPasswordPage;
